import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faNewspaper,
  faBullhorn,
  faLock,
  faEye,
  faEyeSlash,
  faCamera,
  faMicrophone,
  faUsers,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PodcastLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("login"); // 'login' or 'otp'
  const [formData, setFormData] = useState({ email: "" });
  const [otp, setOtp] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value.toLowerCase().trim() });
    setError("");
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    setError("");
  };

  // Step 1: Send OTP (adapted to use axios and your API style)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      // Send OTP request
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/podcast/send/otp/login`, {
        email: formData.email
      },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("OTP send Response:", response);

      if (response.status === 200) {
        toast.success("OTP sent successfully");
        setStep("otp");
      } else {
        setError("Failed to send OTP");
      }
    } catch (err) {
      if (err.response.status === 500) {
        setError("Internal server error. Please try again later.");
      } else {
        if (err.response.status === 404) {
          toast.error("User not found with provided email address");
        } else {
          setError(err.response?.data?.message || "Failed to send OTP");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/podcast/verify/otp/login`, {
        email: formData.email,
        loginOtp: otp
      });
      const data = response.data;
      console.log("Login Response:", data);

      if (data?.success && data?.data?.token) {
        // Save token and user data to localStorage
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('userRole', 'podcastuser');
        localStorage.setItem('userData', JSON.stringify(data.data.user));

        // Debug logging
        console.log("Token saved:", data.data.token);
        console.log("User role saved:", 'podcastuser');
        console.log("User data saved:", data.data.user);

        // Show success message
        toast.success("Login successful! Redirecting to dashboard...");

        // Redirect to podcast dashboard after a short delay
        setTimeout(() => {
          console.log("Redirecting to /podcast");
          navigate('/podcast');
        }, 1500);
      } else {
        console.log("Login failed:", data);
        setError(data?.message || "OTP verification failed");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError("Invalid OTP. Please try again.");
      } else {
        setError(err.response?.data?.message || "OTP verification failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="w-full max-w-5xl mx-auto bg-white rounded-[2rem] shadow-2xl shadow-emerald-200/50 overflow-hidden border border-slate-100">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Left side - Company Branding */}
          <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-emerald-700 via-green-600 to-emerald-900 relative overflow-hidden text-white">
            {/* Background Pattern Overlay */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }}></div>

            {/* Animated Background Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                opacity: [0.05, 0.1, 0.05]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-32 -mt-32"
            ></motion.div>

            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                x: [0, 50, 0],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-400 rounded-full blur-3xl -ml-20 -mb-20"
            ></motion.div>

            <div className="relative flex flex-col justify-between h-full p-12 z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                  <div className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse shadow-[0_0_8px_rgba(110,231,183,0.8)]"></div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-100">Live Studio Portal</span>
                </div>

                <div className="w-20 h-20 mb-8 bg-gradient-to-tr from-white/20 to-white/5 backdrop-blur-xl border border-white/30 rounded-[2rem] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-emerald-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <FontAwesomeIcon icon={faMicrophone} className="text-3xl text-white drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
                </div>

                <h2 className="text-4xl font-[1000] tracking-tight mb-4 leading-[1.1] drop-shadow-sm">
                  Voice your <span className="text-emerald-300">Stories</span>
                </h2>
                <p className="text-emerald-50/80 font-medium tracking-wide text-base max-w-xs leading-relaxed">
                  The ultimate platform for creators to record, manage, and distribute professional podcast content.
                </p>
              </motion.div>

              <div className="space-y-6">
                <div className="space-y-4">
                  {[
                    { icon: faUser, title: "Create Episodes", desc: "Professional recording studio", color: "emerald", delay: 0.1 },
                    { icon: faNewspaper, title: "Manage Content", desc: "Organize episodes & series", color: "blue", delay: 0.2 },
                    { icon: faBullhorn, title: "Reach Audience", desc: "Grow your listener base", color: "purple", delay: 0.3 }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: item.delay, duration: 0.5 }}
                      className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all duration-300 cursor-default"
                    >
                      <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                        <FontAwesomeIcon icon={item.icon} className={`text-${item.color}-300 text-xl`} />
                      </div>
                      <div>
                        <span className="font-black tracking-wide text-sm block group-hover:text-emerald-300 transition-colors uppercase">{item.title}</span>
                        <p className="text-[11px] text-emerald-100/60 uppercase tracking-widest font-bold font-sans">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-8 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-emerald-100/60">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-xs" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Secure Access</span>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-6 h-6 rounded-full border-2 border-emerald-800 bg-emerald-600 flex items-center justify-center text-[8px] font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="w-full lg:w-7/12 p-8 sm:p-12 flex flex-col justify-center bg-white relative">
            {/* Mobile Hearing - Panel Name */}
            <div className="lg:hidden text-center mb-6 border-b border-slate-100 pb-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-emerald-50 rounded-2xl flex items-center justify-center shadow-inner">
                <FontAwesomeIcon icon={faMicrophone} className="text-xl text-emerald-600" />
              </div>
              <h2 className="text-2xl font-[1000] text-emerald-900 uppercase tracking-tight">
                Podcast Studio
              </h2>
            </div>

            <div className="mb-8 group">
              <h1 className="text-3xl sm:text-4xl font-[1000] text-slate-900 tracking-tighter mb-4 uppercase leading-[1.1]">
                Sign In <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Podcast Studio</span>
              </h1>
              <div className="w-12 h-1.5 bg-emerald-600 rounded-full mb-4 transition-all duration-500 group-hover:w-20"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-emerald-600 rounded-full"></span>
                Secure access to your Podcast Studio
              </p>
            </div>

            {step === "login" && (
              <form className="space-y-4" onSubmit={handleSubmit}>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Email Address
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter your registered email address"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-200/50 text-sm font-black text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </div>

                <div className="text-center mt-5">
                  <span className="text-xs font-bold text-slate-500">Don't have an account? </span>
                  <Link to="/podcast/register" className="text-emerald-600 hover:text-emerald-700 font-black text-xs uppercase tracking-widest pl-1">
                    Sign up
                  </Link>
                </div>
              </form>
            )}

            {step === "otp" && (
              <form className="space-y-4" onSubmit={handleOtpSubmit}>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="otp" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Enter OTP
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                    </div>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      required
                      value={otp}
                      onChange={handleOtpChange}
                      className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter the OTP sent to your email"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-indigo-200/50 text-sm font-black text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP & Login'
                    )}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 pt-6 border-t-2 border-slate-50">
              {/* Quick Login Buttons */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Alternative Access</p>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <Link
                    to="/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-indigo-50 hover:border-indigo-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faUser} className="text-slate-400 group-hover:text-indigo-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-indigo-600 uppercase tracking-widest">General</span>
                  </Link>

                  <Link
                    to="/press/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-teal-50 hover:border-teal-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faCamera} className="text-slate-400 group-hover:text-teal-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-teal-600 uppercase tracking-widest">Press</span>
                  </Link>

                  <Link
                    to="/raise/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-purple-50 hover:border-purple-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faBullhorn} className="text-slate-400 group-hover:text-purple-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-purple-600 uppercase tracking-widest">Raise</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodcastLogin;
